import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "./EngagementFormsGrid.module.css";
import { Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import CygnusGrid from "../../reusable_components/CygnusGrid/CygnusGrid";
import { CygnusGridRef } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import { logout } from "../../../utils";
import { getFromLS } from "../../../utils/storage.js";
import { ColDef } from "ag-grid-community";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import IntakeFormAIModal from "../../client_onboarding/IntakeFormsGrid/IntakeFormAIModal/IntakeFormAIModal";

const EngagementFormsGrid: React.FC<{ transporter: any }> = ({
  transporter,
}) => {
  const gridRef = useRef<CygnusGridRef>(null);
  const [formData, setFormData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [key, setKey] = useState<number | undefined>(undefined);
  const [showModifyModal, setShowModifyModal] = useState<boolean>(false);
  const [modifyTarget, setModifyTarget] = useState<{
    formId: number;
    templateId: number;
  } | null>(null);

  const availableFeatures = getFromLS("active-features");
  const featureSet = new Set(
    Array.isArray(availableFeatures) ? availableFeatures : []
  );
  const canUseAIWizardModification = featureSet.has("AI_EL_WIZARD_MODIFICATION");

  useEffect(() => localStorage.setItem("url", "myforms"), []);

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/el_forms/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          alert("Session ended. Please log in again.");
        }
        throw new Error("Failed to fetch forms.");
      }

      const data = await response.json();
      setFormData(data?.data || []);
      setKey(data?.data.length);
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const columnDefs: ColDef[] = [
    { field: "FormName", headerName: "Form Name", flex: 1 },
    {
      field: "TemplateName",
      headerName: "Connected Engagement Letter",
      flex: 1,
    },
    { field: "Status", headerName: "Status", flex: 1 },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params: any) => {
        const form = params.data;

        const handleEdit = () => {
          transporter("engagement_form_editor", {
            engagement_form_config_option: {
              clientType: form.ClientTypeName,
              clientTypeCode: form.ClientTypeCode,
              createform_option: {
                id: form.ELFormId,
                templateid: form.TemplateId,
                form_name: form.FormName,
                engagement_letter_template: form.TemplateName,
                is_final: form.Status === "Active",
                form_purpose: form.FormPurpose,
                initial_data: form.InputStructure,
                preview_data: form.PreviewStructure,
              },
            },
          });
        };

        const isDisabled = form.Status === "Processing" || form.Status === "Failed";
        const disabledTooltip =
          form.Status === "Processing"
            ? "Form is currently processing"
            : "Form generation failed";

        return (
          <div className={styles.actionsCell}>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="button-tooltip">{isDisabled ? disabledTooltip : "Edit form structure"}</Tooltip>
              }
            >
              <span style={{ cursor: isDisabled ? "not-allowed" : "auto" }}>
                <Button
                  variant="link"
                  onClick={handleEdit}
                  disabled={isDisabled}
                  style={{ padding: 0, pointerEvents: isDisabled ? "none" : "auto" }}
                >
                  <Icon.PencilSquare
                    color={isDisabled ? "var(--bs-secondary)" : "var(--theme1_primary)"}
                  />
                </Button>
              </span>
            </OverlayTrigger>
            {canUseAIWizardModification && (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="button-tooltip">{isDisabled ? disabledTooltip : "Modify with AI"}</Tooltip>
                }
              >
                <span style={{ cursor: isDisabled ? "not-allowed" : "auto" }}>
                  <Button
                    variant="link"
                    disabled={isDisabled}
                    style={{ padding: 0, pointerEvents: isDisabled ? "none" : "auto" }}
                    onClick={() => {
                      setModifyTarget({
                        formId: form.ELFormId,
                        templateId: form.TemplateId,
                      });
                      setShowModifyModal(true);
                    }}
                  >
                    <Icon.Magic
                      color={isDisabled ? "var(--bs-secondary)" : "var(--theme1_primary)"}
                    />
                  </Button>
                </span>
              </OverlayTrigger>
            )}
          </div>
        );
      },
      width: 80,
      maxWidth: 80,
      filter: false,
      pinned: "right",
    },
  ];

  return (
    <>
      <IntakeFormAIModal
        mode="modify"
        showFieldPicker={false}
        show={showModifyModal}
        onHide={() => {
          setShowModifyModal(false);
          setModifyTarget(null);
        }}
        onDone={fetchForms}
        formId={modifyTarget?.formId}
        templateId={modifyTarget?.templateId}
        formType="EL Wizard"
      />
      <Row className="no-gutter">
        <Col md={12} className={styles.headerWrapper}>
          <Row>
            <Col md={12}>
              <p className="display-6">Engagement Forms</p>
            </Col>
          </Row>
          <Row>
            <Col md={12} className={styles.actionsRow}>
              <CustomButton
                onclick={() =>
                  transporter("engagement_form_editor", { engagement_form_config_option: {} })
                }
                content="Create Form"
              />
              <div className={styles.refreshContainer}>
                {isLoading ? (
                  <ThreeCircles
                    height="20"
                    width="20"
                    visible={true}
                    ariaLabel="three-circles-rotating"
                    outerCircleColor="var(--theme1_tertiary)"
                    innerCircleColor="var(--theme1_secondary)"
                    middleCircleColor="var(--theme1_primary)"
                  />
                ) : (
                  <Icon.ArrowCounterclockwise
                    onClick={fetchForms}
                    className={styles.refreshIcon}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Col>
        <Col md={12} className={styles.formsGridContainer}>
          <CygnusGrid
            key={key}
            ref={gridRef}
            columnDefs={columnDefs}
            fetchData={async () => {
              return { records: formData, totalCount: formData.length };
            }}
            rowModelType="clientSide"
            rowSelection="single"
            dataIdentifier="WizardPageId"
            cachedIsLoading={isLoading}
            onLoadingChange={setIsLoading}
            cachedData={formData}
            setCachedData={setFormData}
          />
        </Col>
      </Row>
    </>
  );
};

export default EngagementFormsGrid;
